/**
 * OTC Trading API Service Module
 *
 * Handles OTC (Over-The-Counter) trading operations including order management,
 * order book queries, and trade history.
 *
 * Most endpoints require Bearer token authentication (automatically injected).
 *
 * @module api/otc
 * @example
 * ```typescript
 * import { otcAPI } from '@/api';
 *
 * // Get current order book
 * const book = await otcAPI.getOrderBook({ depth: 10 });
 *
 * // Place a buy order
 * const order = await otcAPI.placeOrder({
 *   side: 'buy',
 *   amount_uo: '1000',
 *   unit_price: '1.05'
 * });
 * ```
 */

import { api } from './client';
import type {
  OrderBook,
  ListResponse,
  OtcOrderRow,
  OtcTradeRow,
  OtcOrderRequest,
  OtcCancelRequest,
  OtcOrderResponse,
  PaginationParams,
} from './types';

export type OtcHallDetailBuyer = {
  index: number;
  buyer_id: string;
  avatar: string | null;
  quantity_uo: string;
  unit_price: string;
  deal_time: string;
};

export type OtcHallDetail = {
  id: string;
  display_id: string;
  seller: {
    display_id: string;
    avatar: string | null;
    nickname: string | null;
  };
  listed_uo: string;
  burn_uo: string;
  traded_uo: string;
  unit_price: string;
  value_ap: string;
  pending_uo: string;
  create_time: string;
  update_time: string;
  buyers: OtcHallDetailBuyer[];
};

export type OtcMyTradeRow = {
  id: string;
  php_log_id: string;
  side: 'buy' | 'sell';
  trade_kind_zh: string;
  guide_sales_round: number | null;
  guide_sales_label: string;
  player_number: string;
  avatar: string | null;
  amount_uo: string;
  unit_price: string;
  ap_deal: string;
  sell_type: string;
  status: string;
  status_zh: string;
  create_time: string;
  update_time: string;
};

/**
 * OTC Trading API endpoints
 */
export const otcAPI = {
  /**
   * GET /api/otc/book
   *
   * Fetch the current OTC order book snapshot.
   * Shows best buy and sell orders at different price levels.
   *
   * @param params - Query parameters
   * @param params.depth - Number of price levels to return (default: 10)
   * @returns Order book with buy and sell sides
   *
   * @example
   * ```typescript
   * const book = await otcAPI.getOrderBook({ depth: 20 });
   *
   * // Returns:
   * // {
   * //   buy: [
   * //     { price: "1.05", quantity: "500", count: 3 },
   * //     { price: "1.04", quantity: "1000", count: 5 },
   * //   ],
   * //   sell: [
   * //     { price: "1.06", quantity: "750", count: 2 },
   * //     { price: "1.07", quantity: "600", count: 4 },
   * //   ]
   * // }
   * ```
   *
   * @required_headers
   * - None (public endpoint)
   *
   * @throws ApiError on server error
   */
  getOrderBook: async (params?: { depth?: number }): Promise<OrderBook> => {
    const raw = await api.get<{
      bids: Array<{ price: string; amount_uo: string; count: number }>;
      asks: Array<{ price: string; amount_uo: string; count: number }>;
    }>('/api/otc/book', params);
    return {
      buy: raw.bids.map((b) => ({ price: b.price, quantity: b.amount_uo, count: b.count })),
      sell: raw.asks.map((a) => ({ price: a.price, quantity: a.amount_uo, count: a.count })),
    };
  },

  getHall: (params?: PaginationParams & { side?: string; status?: string }) =>
    api.get<{ total: number; page: number; limit: number; rows: Record<string, unknown>[] }>(
      '/api/otc/hall',
      params,
    ),

  getHallDetail: (id: string) => api.get<OtcHallDetail>(`/api/otc/hall/${encodeURIComponent(id)}`),

  getMyTrades: (params?: {
    page?: number;
    limit?: number;
    side?: 'buy' | 'sell';
    account_id?: number;
  }) =>
    api.get<{ total: number; page: number; limit: number; rows: OtcMyTradeRow[] }>(
      '/api/otc/my-trades',
      params,
    ),

  getGuide: () =>
    api.get<{
      pair: string;
      screens: Array<{ screen_zh: string; method: string; path: string }>;
    }>('/api/otc/guide'),

  /**
   * GET /api/otc/orders
   *
   * Fetch paginated list of orders for the current user.
   * Shows both active and filled/cancelled orders.
   *
   * @param params - Pagination parameters
   * @param params.page - Page number (1-indexed)
   * @param params.limit - Items per page
   * @returns Paginated list of user's orders
   *
   * @example
   * ```typescript
   * const orders = await otcAPI.getOrders({
   *   page: 1,
   *   limit: 50
   * });
   *
   * // Returns:
   * // {
   * //   total: 15,
   * //   rows: [
   * //     {
   * //       order_id: 101,
   * //       user_id: 42,
   * //       side: "buy",
   * //       amount_uo: "5000",
   * //       unit_price: "1.05",
   * //       filled_quantity: "1500",
   * //       status: "partial",
   * //       create_time: "2024-01-15T09:00:00Z",
   * //       update_time: "2024-01-15T10:30:00Z"
   * //     }
   * //   ]
   * // }
   * ```
   *
   * @required_headers
   * - Authorization: "Bearer <access_token>" (automatically injected)
   *
   * @throws ApiError if token is invalid or expired
   */
  getOrders: async (params?: PaginationParams): Promise<ListResponse<OtcOrderRow>> =>
    api.get<ListResponse<OtcOrderRow>>('/api/otc/orders', params),

  /**
   * GET /api/otc/trades
   *
   * Fetch paginated list of trades the current user has executed.
   * Shows completed buy/sell trades.
   *
   * @param params - Pagination parameters
   * @param params.page - Page number (1-indexed)
   * @param params.limit - Items per page
   * @returns Paginated list of user's trades
   *
   * @example
   * ```typescript
   * const trades = await otcAPI.getTrades({
   *   page: 1,
   *   limit: 50
   * });
   *
   * // Returns:
   * // {
   * //   total: 42,
   * //   rows: [
   * //     {
   * //       trade_id: 501,
   * //       buyer_id: 42,
   * //       seller_id: 43,
   * //       quantity: "500",
   * //       unit_price: "1.05",
   * //       total_price: "525.00",
   * //       status: "completed",
   * //       create_time: "2024-01-15T10:30:00Z"
   * //     }
   * //   ]
   * // }
   * ```
   *
   * @required_headers
   * - Authorization: "Bearer <access_token>" (automatically injected)
   *
   * @throws ApiError if token is invalid or expired
   */
  getTrades: async (params?: PaginationParams): Promise<ListResponse<OtcTradeRow>> =>
    api.get<ListResponse<OtcTradeRow>>('/api/otc/trades', params),

  /**
   * POST /api/otc/orders
   *
   * Create a new OTC order (buy or sell).
   * Order will be matched against existing orders in the order book.
   *
   * @param request - Order details
   * @param request.side - "buy" or "sell"
   * @param request.amount_uo - Total amount of UO (for buy) or quantity of UO (for sell)
   * @param request.unit_price - Price per unit
   * @returns Created order details
   *
   * @example
   * ```typescript
   * // Place a buy order for 5000 UO at 1.05 per unit
   * const order = await otcAPI.placeOrder({
   *   side: 'buy',
   *   amount_uo: '5000',
   *   unit_price: '1.05'
   * });
   *
   * // Returns:
   * // {
   * //   order_id: 101,
   * //   side: "buy",
   * //   amount_uo: "5000",
   * //   unit_price: "1.05",
   * //   filled_quantity: "0",
   * //   status: "new",
   * //   create_time: "2024-01-15T10:35:00Z"
   * // }
   * ```
   *
   * @required_headers
   * - Authorization: "Bearer <access_token>" (automatically injected)
   * - Content-Type: "application/json" (automatically set)
   *
   * @throws ApiError if:
   * - Token is invalid or expired
   * - Insufficient balance
   * - Invalid side value
   * - Price/amount validation fails
   */
  placeOrder: async (request: OtcOrderRequest): Promise<OtcOrderResponse> =>
    api.post<OtcOrderResponse>('/api/otc/orders', request),

  /**
   * POST /api/otc/orders/cancel
   *
   * Cancel an existing order that hasn't been fully filled.
   * Only orders in "new" or "partial" status can be cancelled.
   *
   * @param request - Cancel request
   * @param request.order_id - ID of order to cancel
   * @returns Updated order with status "cancelled"
   *
   * @example
   * ```typescript
   * const cancelled = await otcAPI.cancelOrder({
   *   order_id: 101
   * });
   *
   * // Returns:
   * // {
   * //   order_id: 101,
   * //   side: "buy",
   * //   amount_uo: "5000",
   * //   unit_price: "1.05",
   * //   filled_quantity: "1500",
   * //   status: "cancelled",
   * //   create_time: "2024-01-15T10:35:00Z"
   * // }
   * ```
   *
   * @required_headers
   * - Authorization: "Bearer <access_token>" (automatically injected)
   * - Content-Type: "application/json" (automatically set)
   *
   * @throws ApiError if:
   * - Token is invalid or expired
   * - Order not found
   * - Order already filled or cancelled
   */
  cancelOrder: async (request: OtcCancelRequest): Promise<OtcOrderResponse> =>
    api.post<OtcOrderResponse>('/api/otc/orders/cancel', request),

  /** Sell-UO / extended body (asset, transaction_password, etc.) */
  placeOrderRaw: async (body: Record<string, unknown>) =>
    api.post<Record<string, unknown>>('/api/otc/orders', body),

  /** GET /api/otc/buy-queue — 購買隊列 (legacy hang_buy) */
  getBuyQueue: async (params?: { page?: number; limit?: number; search?: string }) =>
    api.get<{
      total: number;
      page: number;
      limit: number;
      new_number: string;
      rows: Array<{
        buyer_id: string;
        queue_funds: string;
        status_zh: string;
        queue_time: string;
      }>;
    }>('/api/otc/buy-queue', params),
};
